import type { SharePointEntityBulkTarget } from "../../api/types.ts";
import {
    type ODataSelection,
    areODataSelectionEqual,
    areSelectionQueriesEqual,
    getSingleActionId,
    isIncludedInSelection
} from "@sg-protect/odata";

export interface SharePointEntitySelection {
    entity: "permissionMatrix";
    targets: SharePointEntityBulkTarget[];
    rowKeys: string[];
}

export type RemediationSelection = ODataSelection | SharePointEntitySelection;

export function isSharePointEntitySelection(selection: RemediationSelection): selection is SharePointEntitySelection {
    return selection.entity === "permissionMatrix";
}

function areTargetsEqual(a: SharePointEntityBulkTarget, b: SharePointEntityBulkTarget): boolean {
    const nodePath = a.nodePath ?? [];
    const otherNodePath = b.nodePath ?? [];

    return (
        a.siteCollectionId === b.siteCollectionId &&
        a.path === b.path &&
        a.isFromSiteCollectionAdmin === b.isFromSiteCollectionAdmin &&
        nodePath.length === otherNodePath.length &&
        nodePath.every((id, nodeIndex) => id === otherNodePath[nodeIndex])
    );
}

function doesSharePointEntitySelectionCover(
    running: SharePointEntitySelection,
    row: SharePointEntitySelection
): boolean {
    return row.targets.every(rowTarget => running.targets.some(target => areTargetsEqual(target, rowTarget)));
}

export function doesRunningSelectionCoverRow(running: RemediationSelection, row: RemediationSelection): boolean {
    if (isSharePointEntitySelection(row)) {
        return isSharePointEntitySelection(running) && doesSharePointEntitySelectionCover(running, row);
    }

    if (isSharePointEntitySelection(running)) {
        return false;
    }

    if (areODataSelectionEqual(running, row)) {
        return true;
    }

    const rowId = getSingleActionId(row);

    if (running.entity !== row.entity || rowId === undefined) {
        return false;
    }

    const hasExplicitIncludes = Boolean(running.includes?.length);

    return isIncludedInSelection(running, rowId) && (hasExplicitIncludes || areSelectionQueriesEqual(running, row));
}
